import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw, convertFromHTML } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  FileCode,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Copy,
  FileText,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  HelpCircle,
  User,
  Car,
  FileSignature,
  Calendar,
  Printer
} from 'lucide-react';

const tipoLabels = {
  contrato: 'Contrato',
  aditivo: 'Aditivo',
  termo: 'Termo',
  acordo: 'Acordo',
  recibo: 'Recibo',
  outro: 'Outro'
};

// Complete list of placeholders
const placeholderGroups = [
  {
    category: 'Cliente',
    icon: User,
    items: [
      { key: '{{cliente.nome}}', desc: 'Nome completo do cliente' },
      { key: '{{cliente.cpf_cnpj}}', desc: 'CPF ou CNPJ do cliente' },
      { key: '{{cliente.rg}}', desc: 'RG do cliente' },
      { key: '{{cliente.telefone}}', desc: 'Telefone do cliente' },
      { key: '{{cliente.email}}', desc: 'E-mail do cliente' },
      { key: '{{cliente.endereco}}', desc: 'Endereço completo' },
      { key: '{{cliente.cep}}', desc: 'CEP do cliente' },
      { key: '{{cliente.cidade}}', desc: 'Cidade do cliente' },
      { key: '{{cliente.estado}}', desc: 'Estado do cliente' },
    ]
  },
  {
    category: 'Veículo',
    icon: Car,
    items: [
      { key: '{{veiculo.id}}', desc: 'ID do veículo' },
      { key: '{{veiculo.marca}}', desc: 'Marca do veículo' },
      { key: '{{veiculo.modelo}}', desc: 'Modelo do veículo' },
      { key: '{{veiculo.ano_modelo}}', desc: 'Ano/Modelo do veículo' },
      { key: '{{veiculo.cor}}', desc: 'Cor do veículo' },
      { key: '{{veiculo.placa}}', desc: 'Placa do veículo' },
      { key: '{{veiculo.renavam}}', desc: 'RENAVAM do veículo' },
      { key: '{{veiculo.chassi}}', desc: 'Chassi do veículo' },
      { key: '{{veiculo.km_atual}}', desc: 'Quilometragem atual' },
    ]
  },
  {
    category: 'Contrato',
    icon: FileSignature,
    items: [
      { key: '{{contrato.numero}}', desc: 'Número do contrato' },
      { key: '{{contrato.data_inicio}}', desc: 'Data de início (DD/MM/YYYY)' },
      { key: '{{contrato.data_inicio_extenso}}', desc: 'Data início por extenso' },
      { key: '{{contrato.data_fim}}', desc: 'Data final (DD/MM/YYYY)' },
      { key: '{{contrato.data_fim_extenso}}', desc: 'Data final por extenso' },
      { key: '{{contrato.prazo_meses}}', desc: 'Duração do contrato em meses' },
      { key: '{{contrato.valor_parcela}}', desc: 'Valor da parcela (R$)' },
      { key: '{{contrato.valor_total}}', desc: 'Valor total (R$)' },
      { key: '{{contrato.multa_diaria}}', desc: 'Multa diária (R$)' },
      { key: '{{contrato.periodicidade}}', desc: 'Periodicidade (semanal/mensal)' },
      { key: '{{contrato.quantidade_parcelas}}', desc: 'Quantidade de parcelas' },
    ]
  },
  {
    category: 'Data/Assinatura',
    icon: Calendar,
    items: [
      { key: '{{data_hoje}}', desc: 'Data atual (DD/MM/YYYY)' },
      { key: '{{data_hoje_extenso}}', desc: 'Data atual por extenso' },
      { key: '{{imagem_assinatura}}', desc: 'Imagem da assinatura digital' },
    ]
  }
];

// Quick insert placeholders
const quickInsertPlaceholders = [
  '{{cliente.nome}}',
  '{{cliente.cpf_cnpj}}',
  '{{veiculo.marca}}',
  '{{veiculo.modelo}}',
  '{{veiculo.placa}}',
  '{{contrato.numero}}',
  '{{contrato.data_inicio}}',
  '{{contrato.data_fim}}',
  '{{contrato.prazo_meses}}',
  '{{data_hoje_extenso}}',
  '{{imagem_assinatura}}'
];

export default function TemplatesPage() {
  const { API, user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [tipoFilter, setTipoFilter] = useState('todos');
  const textareaRef = useRef(null);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPlaceholdersModal, setShowPlaceholdersModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formMode, setFormMode] = useState('create');
  
  // Form state
  const [formData, setFormData] = useState({
    nome: '',
    tipo: 'contrato',
    conteudo: '',
    status: 'ativo',
    imagem_assinatura: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, [search, statusFilter, tipoFilter]);

  const loadTemplates = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter !== 'todos') params.append('status', statusFilter);
      if (tipoFilter !== 'todos') params.append('tipo', tipoFilter);
      
      const response = await axios.get(`${API}/templates?${params}`, { withCredentials: true });
      setTemplates(response.data);
    } catch (error) {
      toast.error('Erro ao carregar modelos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Convert EditorState to HTML
    const contentHTML = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    const finalData = { ...formData, conteudo: contentHTML };
    
    if (!finalData.nome.trim()) {
      toast.error('Informe o nome do modelo');
      return;
    }
    
    if (!contentHTML.trim() || contentHTML === '<p></p>\n') {
      toast.error('O conteúdo do modelo não pode estar vazio');
      return;
    }
    
    setSubmitting(true);
    
    try {
      if (formMode === 'create') {
        await axios.post(`${API}/templates`, finalData, { withCredentials: true });
        toast.success('Modelo criado com sucesso!');
      } else {
        await axios.put(`${API}/templates/${selectedTemplate.template_id}`, finalData, { withCredentials: true });
        toast.success('Modelo atualizado com sucesso!');
      }
      setShowModal(false);
      resetForm();
      setEditorState(EditorState.createEmpty());
      loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar modelo');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/templates/${selectedTemplate.template_id}`, { withCredentials: true });
      toast.success('Modelo excluído com sucesso!');
      setShowDeleteDialog(false);
      loadTemplates();
    } catch (error) {
      toast.error('Erro ao excluir modelo');
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      tipo: 'contrato',
      conteudo: '',
      status: 'ativo',
      imagem_assinatura: ''
    });
    setEditorState(EditorState.createEmpty());
  };

  const openCreateModal = () => {
    setFormMode('create');
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (template) => {
    setFormMode('edit');
    setSelectedTemplate(template);
    setFormData({
      nome: template.nome,
      tipo: template.tipo,
      conteudo: template.conteudo,
      status: template.status,
      imagem_assinatura: template.imagem_assinatura || ''
    });
    
    // Convert HTML to EditorState
    try {
      const blocksFromHTML = convertFromHTML(template.conteudo || '');
      const contentState = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
      );
      setEditorState(EditorState.createWithContent(contentState));
    } catch (error) {
      setEditorState(EditorState.createEmpty());
    }
    
    setShowModal(true);
  };

  const openViewModal = (template) => {
    setSelectedTemplate(template);
    setShowViewModal(true);
  };

  const openDeleteDialog = (template) => {
    setSelectedTemplate(template);
    setShowDeleteDialog(true);
  };

  const insertPlaceholder = (placeholder) => {
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = formData.conteudo;
      const newText = text.substring(0, start) + placeholder + text.substring(end);
      setFormData({ ...formData, conteudo: newText });
      
      // Set cursor position after the inserted placeholder
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + placeholder.length, start + placeholder.length);
      }, 0);
    } else {
      setFormData({ ...formData, conteudo: formData.conteudo + placeholder });
    }
  };

  const copyTemplate = async (template) => {
    try {
      const newTemplate = {
        nome: `${template.nome} (Cópia)`,
        tipo: template.tipo,
        conteudo: template.conteudo,
        status: 'ativo',
        imagem_assinatura: template.imagem_assinatura
      };
      await axios.post(`${API}/templates`, newTemplate, { withCredentials: true });
      toast.success('Modelo duplicado com sucesso!');
      loadTemplates();
    } catch (error) {
      toast.error('Erro ao duplicar modelo');
    }
  };

  const filteredTemplates = templates.filter(t => {
    if (!search) return true;
    const s = search.toLowerCase();
    return t.nome.toLowerCase().includes(s) || t.tipo.toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6" data-testid="templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
            <FileCode className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold font-[Manrope] text-gray-900">Modelos de Documentos</h1>
            <p className="text-gray-500 text-sm">Crie e gerencie templates de contratos</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowPlaceholdersModal(true)}
            data-testid="view-placeholders-button"
          >
            <HelpCircle className="w-4 h-4 mr-2" />
            Ver Placeholders
          </Button>
          <Button onClick={openCreateModal} className="bg-violet-500 hover:bg-violet-600" data-testid="add-template-button">
            <Plus className="w-4 h-4 mr-2" />
            Novo Modelo
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por nome ou tipo..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-templates"
              />
            </div>
            <Select value={tipoFilter} onValueChange={setTipoFilter}>
              <SelectTrigger className="w-[150px]" data-testid="tipo-filter">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os tipos</SelectItem>
                <SelectItem value="contrato">Contrato</SelectItem>
                <SelectItem value="aditivo">Aditivo</SelectItem>
                <SelectItem value="termo">Termo</SelectItem>
                <SelectItem value="acordo">Acordo</SelectItem>
                <SelectItem value="recibo">Recibo</SelectItem>
                <SelectItem value="outro">Outro</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="ativo">Ativos</SelectItem>
                <SelectItem value="inativo">Inativos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Templates List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64 text-gray-500">
            <FileCode className="w-12 h-12 mb-2 opacity-50" />
            <p>Nenhum modelo encontrado</p>
            <Button onClick={openCreateModal} variant="link" className="mt-2">
              Criar primeiro modelo
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <Card 
              key={template.template_id} 
              className="hover:shadow-md transition-shadow"
              data-testid={`template-card-${template.template_id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-violet-500" />
                    <CardTitle className="text-base font-semibold truncate" title={template.nome}>
                      {template.nome}
                    </CardTitle>
                  </div>
                  <Badge variant={template.status === 'ativo' ? 'default' : 'secondary'}>
                    {template.status === 'ativo' ? 'Ativo' : 'Inativo'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <Badge variant="outline" className="text-xs">
                    {tipoLabels[template.tipo] || template.tipo}
                  </Badge>
                  
                  <p className="text-sm text-gray-500 line-clamp-2">
                    {template.conteudo.substring(0, 100)}...
                  </p>
                  
                  <div className="flex gap-2 pt-2 border-t">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => openViewModal(template)}
                      data-testid={`view-template-${template.template_id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Ver
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => openEditModal(template)}
                      data-testid={`edit-template-${template.template_id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => copyTemplate(template)}
                      title="Duplicar modelo"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => openDeleteDialog(template)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-template-${template.template_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[95vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {formMode === 'create' ? 'Novo Modelo de Documento' : 'Editar Modelo'}
            </DialogTitle>
          </DialogHeader>
          
          {/* Botões no topo - sempre visíveis */}
          <div className="flex items-center justify-end gap-2 pb-3 border-b">
            <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-violet-500 hover:bg-violet-600"
              data-testid="save-template-button"
            >
              {submitting ? 'Salvando...' : 'Salvar Modelo'}
            </Button>
          </div>

          {/* Conteúdo com scroll */}
          <div className="flex-1 overflow-y-auto pr-2">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="nome">Nome do Modelo *</Label>
                  <Input
                    id="nome"
                    placeholder="Ex: Contrato de Locação"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    data-testid="form-nome"
                  />
                </div>
                <div>
                  <Label htmlFor="tipo">Tipo *</Label>
                  <Select 
                    value={formData.tipo} 
                    onValueChange={(value) => setFormData({ ...formData, tipo: value })}
                  >
                    <SelectTrigger data-testid="form-tipo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="contrato">Contrato</SelectItem>
                      <SelectItem value="aditivo">Aditivo</SelectItem>
                      <SelectItem value="termo">Termo</SelectItem>
                      <SelectItem value="acordo">Acordo</SelectItem>
                      <SelectItem value="recibo">Recibo</SelectItem>
                      <SelectItem value="outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select 
                    value={formData.status} 
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger data-testid="form-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ativo">Ativo</SelectItem>
                      <SelectItem value="inativo">Inativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Quick Insert Placeholders */}
              <div>
                <Label className="text-xs text-gray-500 mb-2 block">Inserir Placeholders Rápidos:</Label>
                <div className="flex flex-wrap gap-1">
                  {quickInsertPlaceholders.map((p) => (
                    <Button
                      key={p}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs h-7 px-2 bg-violet-50 border-violet-200 text-violet-700 hover:bg-violet-100"
                      onClick={() => insertPlaceholder(p)}
                    >
                      {p}
                    </Button>
                  ))}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => setShowPlaceholdersModal(true)}
                  >
                    <HelpCircle className="w-3 h-3 mr-1" />
                    Ver Todos
                  </Button>
                </div>
              </div>

              {/* Content Editor com altura fixa e scroll */}
              <div>
                <Label htmlFor="conteudo">Conteúdo do Modelo *</Label>
                
                {/* Line Height Selector */}
                <div className="flex items-center gap-2 my-2 text-sm">
                  <span className="text-gray-600">Espaçamento:</span>
                  <select 
                    className="border rounded px-2 py-1 text-sm"
                    onChange={(e) => {
                      const lineHeight = e.target.value;
                      document.querySelector('.demo-editor').style.lineHeight = lineHeight;
                    }}
                    defaultValue="1.6"
                  >
                    <option value="1.0">Simples (1.0)</option>
                    <option value="1.15">1.15</option>
                    <option value="1.5">1.5</option>
                    <option value="1.6">1.6</option>
                    <option value="2.0">Duplo (2.0)</option>
                    <option value="2.5">2.5</option>
                    <option value="3.0">Triplo (3.0)</option>
                  </select>
                </div>

                {/* Editor com altura fixa e scroll interno */}
                <div className="border rounded-md bg-white" style={{ height: '500px', overflow: 'hidden' }}>
                  <Editor
                    editorState={editorState}
                    onEditorStateChange={setEditorState}
                    wrapperClassName="demo-wrapper"
                    editorClassName="demo-editor px-3"
                    editorStyle={{ 
                      height: '400px',
                      overflowY: 'auto',
                      padding: '12px'
                    }}
                    toolbar={{
                    options: ['inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign', 'colorPicker', 'link', 'remove', 'history'],
                    inline: { 
                      inDropdown: false,
                      options: ['bold', 'italic', 'underline', 'strikethrough', 'superscript', 'subscript'],
                    },
                    blockType: {
                      inDropdown: true,
                      options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote', 'Code'],
                      className: 'rdw-block-dropdown',
                    },
                    fontSize: {
                      inDropdown: true,
                      options: [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 60, 72, 96],
                      className: 'rdw-fontsize-dropdown',
                    },
                    fontFamily: {
                      inDropdown: true,
                      options: ['Arial', 'Georgia', 'Impact', 'Tahoma', 'Times New Roman', 'Verdana', 'Courier New', 'Comic Sans MS'],
                      className: 'rdw-fontfamily-dropdown',
                    },
                    list: { 
                      inDropdown: false,
                      options: ['unordered', 'ordered', 'indent', 'outdent'] 
                    },
                    textAlign: { 
                      inDropdown: false,
                      options: ['left', 'center', 'right', 'justify'] 
                    },
                    colorPicker: {
                      colors: ['rgb(0,0,0)', 'rgb(68,68,68)', 'rgb(102,102,102)', 'rgb(153,153,153)', 
                               'rgb(204,0,0)', 'rgb(230,145,56)', 'rgb(255,255,0)', 'rgb(0,128,0)',
                               'rgb(0,0,255)', 'rgb(102,0,204)', 'rgb(255,0,255)', 'rgb(255,255,255)'],
                    },
                    link: {
                      inDropdown: false,
                      options: ['link', 'unlink'],
                    },
                    history: {
                      inDropdown: false,
                      options: ['undo', 'redo'],
                    },
                  }}
                  localization={{
                    locale: 'pt',
                    translations: {
                      'generic.add': 'Adicionar',
                      'generic.cancel': 'Cancelar',
                      'components.controls.blocktype.normal': 'Normal',
                      'components.controls.blocktype.h1': 'Título 1',
                      'components.controls.blocktype.h2': 'Título 2',
                      'components.controls.blocktype.h3': 'Título 3',
                      'components.controls.blocktype.h4': 'Título 4',
                      'components.controls.blocktype.h5': 'Título 5',
                      'components.controls.blocktype.h6': 'Título 6',
                      'components.controls.blocktype.blockquote': 'Citação',
                      'components.controls.blocktype.code': 'Código',
                      'components.controls.fontsize.fontsize': 'Tamanho',
                      'components.controls.fontfamily.fontfamily': 'Fonte',
                    }
                  }}
                  placeholder="Digite o conteúdo do documento aqui...

Exemplo:

CONTRATO DE LOCAÇÃO DE VEÍCULO

Pelo presente instrumento, de um lado {{cliente.nome}}, inscrito no CPF sob o nº {{cliente.cpf_cnpj}}, residente em {{cliente.endereco}}, doravante denominado LOCATÁRIO...

Duração do contrato: {{contrato.prazo_meses}} meses

São Luís - MA, {{data_hoje_extenso}}

{{imagem_assinatura}}"
                  />
                </div>
              </div>

              {/* Tip */}
              <div className="bg-violet-50 p-3 rounded-md mt-3 text-sm">
                <p className="text-violet-700">
                  <strong>Dica:</strong> Use os placeholders acima para inserir dados dinâmicos. 
                  Eles serão substituídos automaticamente ao gerar o documento para um contrato específico.
                </p>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-violet-500" />
              {selectedTemplate?.nome}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[60vh]">
            <div className="flex items-center gap-2 mb-4">
              <Badge variant="outline">{tipoLabels[selectedTemplate?.tipo] || selectedTemplate?.tipo}</Badge>
              <Badge variant={selectedTemplate?.status === 'ativo' ? 'default' : 'secondary'}>
                {selectedTemplate?.status === 'ativo' ? 'Ativo' : 'Inativo'}
              </Badge>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border">
              <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700">
                {selectedTemplate?.conteudo}
              </pre>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewModal(false)}>
              Fechar
            </Button>
            <Button 
              onClick={() => {
                setShowViewModal(false);
                openEditModal(selectedTemplate);
              }}
              className="bg-violet-500 hover:bg-violet-600"
            >
              <Edit className="w-4 h-4 mr-2" />
              Editar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Placeholders Help Modal */}
      <Dialog open={showPlaceholdersModal} onOpenChange={setShowPlaceholdersModal}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-violet-500" />
              Placeholders Disponíveis
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[60vh]">
            <p className="text-sm text-gray-600 mb-4">
              Use os placeholders abaixo no conteúdo dos seus modelos. Eles serão substituídos 
              automaticamente pelos dados do contrato ao gerar o documento.
            </p>
            
            <Tabs defaultValue={placeholderGroups[0].category} className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                {placeholderGroups.map((group) => (
                  <TabsTrigger key={group.category} value={group.category} className="text-xs">
                    {group.category}
                  </TabsTrigger>
                ))}
              </TabsList>
              
              {placeholderGroups.map((group) => (
                <TabsContent key={group.category} value={group.category} className="mt-4">
                  <div className="space-y-2">
                    {group.items.map((item) => (
                      <div 
                        key={item.key}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border hover:bg-violet-50 hover:border-violet-200 transition-colors cursor-pointer"
                        onClick={() => {
                          navigator.clipboard.writeText(item.key);
                          toast.success(`${item.key} copiado!`);
                        }}
                      >
                        <div>
                          <code className="text-sm font-mono text-violet-600 bg-violet-100 px-2 py-1 rounded">
                            {item.key}
                          </code>
                          <p className="text-sm text-gray-500 mt-1">{item.desc}</p>
                        </div>
                        <Copy className="w-4 h-4 text-gray-400" />
                      </div>
                    ))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlaceholdersModal(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Modelo</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o modelo "{selectedTemplate?.nome}"?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-button"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
